/// <amd-module name="TFS/Admin/Groups/Common/Redux/Actions/MemberOfPivotActions" />
import { createAction, ActionsUnion } from "redux/Utils/ActionHelper";
import { PermissionsGroup, PermissionsGraphGroup } from "TFS/Admin/Groups/Common/Contracts";
import { GraphMember } from "VSS/Platform/Contracts/Graph";
import { MessageCardSeverity } from "VSSUI/MessageCard";
import { IIdentity } from "VSSUI/IdentityPicker";
import { GroupPermissions } from "TFS/Admin/Common/Contracts/OrganizationSettings";
import { IOperationStatus } from "TFS/Admin/Common/Constants/Constants";
import { ObservableArray } from "VSS/Core/Observable";
import { IPermissionPage, SearchStatus, MemberOfOperation } from "TFS/Admin/Groups/Common/Constants";

/**
 * Actions for permissions view
 */
export const MemberOfPivotActions = {
    // Member pivot iniitializers
    initView: (selectedGroup?: PermissionsGroup, selectedUser?: GraphMember, permissionPage?: IPermissionPage) =>
        createAction(MemberOfPivotActionTypes.InitView, { selectedGroup, selectedUser, permissionPage }),
    initialGroupFetch: (groups: PermissionsGraphGroup[]) => createAction(MemberOfPivotActionTypes.InitialGroupFetch, groups),
    disposeView: () => createAction(MemberOfPivotActionTypes.DisposeView),
    membersLoadFailed: () => createAction(MemberOfPivotActionTypes.MembersLoadFailed),

    // Filter Member actions
    searchMembers: (items: ObservableArray<PermissionsGraphGroup>, newKeyword?: string, oldKeyword?: string, permissionPage?: IPermissionPage) =>
        createAction(MemberOfPivotActionTypes.SearchMembers, { items, newKeyword, oldKeyword, permissionPage }),
    updateSearchStatus: (searchStatus: SearchStatus) => createAction(MemberOfPivotActionTypes.UpdateSearchStatus, searchStatus),
    updateSearchResult: (keyword: string, result: any[], hasMore: boolean) =>
        createAction(MemberOfPivotActionTypes.UpdateSearchResult, { result, hasMore, keyword }),

    //Delete Member actions
    onDeleteMemberClick: (member: PermissionsGraphGroup) => createAction(MemberOfPivotActionTypes.DeleteMemberClick, member),
    onDeleteDialogCancel: () => createAction(MemberOfPivotActionTypes.DeleteDialogCancel),
    onDeleteDialogConfirm: (member: GraphMember, parentGroup: PermissionsGraphGroup) =>
        createAction(MemberOfPivotActionTypes.DeleteDialogConfirm, { member, parentGroup }),

    onDismissMessageCard: () => createAction(MemberOfPivotActionTypes.DismissMessageCard),
    onDismissMultiSummary: () => createAction(MemberOfPivotActionTypes.DismissMultiSummary),
    memberOperationStatus: (
        operation: MemberOfOperation,
        resultMessage: string,
        severity: MessageCardSeverity,
        selectedItems?: PermissionsGraphGroup[],
        multiOperationStatuses?: IOperationStatus[]
    ) => createAction(MemberOfPivotActionTypes.MemberOperationStatus, { operation, resultMessage, severity, selectedItems, multiOperationStatuses }),
    // Add Member panel actions
    closeAddMemberOfPanel: () => createAction(MemberOfPivotActionTypes.CloseAddMemberOfPanel),
    openAddMemberOfPanel: () => createAction(MemberOfPivotActionTypes.OpenAddMemberOfPanel),
    onSaveMemberOf: (subjectIdentities: IIdentity[], selectedMember: GraphMember, permissionPage?: IPermissionPage) =>
        createAction(MemberOfPivotActionTypes.SaveMemberOf, { subjectIdentities, selectedMember, permissionPage }),
    getMemberOfGroupPermissions: (subjectDescriptor: string, isSelectedGroup: boolean) =>
        createAction(MemberOfPivotActionTypes.MemberOfGroupPermissions, { subjectDescriptor, isSelectedGroup }),
    getMemberOfGroupPermissionsSuccess: (permissions: GroupPermissions, isSelectedGroup: boolean, subjectDescriptor: string) =>
        createAction(MemberOfPivotActionTypes.MemberOfGroupPermissionsSuccess, { permissions, isSelectedGroup, subjectDescriptor }),

    // Add & remove identities actions
    checkMembershipExistence: (member: string, parentGroup: IIdentity) =>
        createAction(MemberOfPivotActionTypes.CheckMembershipExistence, { member, parentGroup }),
    addInvalidMember: (member: IIdentity) => createAction(MemberOfPivotActionTypes.AddInvalidMember, { member }),
    addValidMember: (member: IIdentity) => createAction(MemberOfPivotActionTypes.AddValidMember, { member }),
    removeMembers: (members: IIdentity[]) => createAction(MemberOfPivotActionTypes.RemoveMembers, { members }),

    onMultiDeleteClick: (member: GraphMember, parentsToDelete: PermissionsGraphGroup[]) =>
        createAction(MemberOfPivotActionTypes.OnMultiDeleteClick, { member, parentsToDelete })
};

export const enum MemberOfPivotActionTypes {
    InitView = "MemberOfPivot/Init",
    DisposeView = "MemberOfPivot/Dispose",
    InitialGroupFetch = "MemberOfPivot/InitialGroupFetch",
    MembersLoadFailed = "MemberOfPivot/MembersLoadFailed",

    // Filter
    SearchMembers = "MemberOfPivot/SearchMembers",
    UpdateSearchStatus = "MemberOfPivot/UpdateSearchStatus",
    UpdateSearchResult = "MemberOfPivot/UpdateSearchResult",

    //Delete
    DeleteMemberClick = "MemberOfPivot/DeleteMemberClick",
    DeleteDialogCancel = "MemberOfPivot/DeleteDialogCancel",
    DeleteDialogConfirm = "MemberOfPivot/DeleteDialogConfirm",

    DismissMessageCard = "MemberOfPivot/DismissMessageCard",
    MemberOperationStatus = "MemberOfPivot/MemberOperationStatus",
    DismissMultiSummary = "MemberOfPivot/DismissMultiSummary",

    //Add Member Panel
    CloseAddMemberOfPanel = "MemberOfPivot/CloseAddMemberOfPanel",
    OpenAddMemberOfPanel = "MemberOfPivot/OpenAddMemberOfPanel",
    SaveMemberOf = "MemberOfPivot/SaveMemberOf",
    MemberOfGroupPermissions = "MemberOfPivot/MemberOfGroupPermissions",
    MemberOfGroupPermissionsSuccess = "MemberOfPivot/MemberOfGroupPermissionsSuccess",

    // Add & remove identities
    CheckMembershipExistence = "MemberOfPivot/CheckMembershipExistence",
    AddInvalidMember = "MemberOfPivot/AddInvalidMember",
    AddValidMember = "MemberOfPivot/AddValidMember",
    RemoveMembers = "MemberOfPivot/RemoveMembers",

    OnMultiDeleteClick = "MemberOfPivot/OnMultiDeleteClick"
}

export type MemberOfPivotActions = ActionsUnion<typeof MemberOfPivotActions>;
